import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  ContentUploadStatus,
  MediaInfo,
  MediaLinkedType,
} from "../../../domain/media";
import { MediaRepository } from "../../../domain/media-repository";
import { UploadService } from "../../../infrastructure/upload/upload-service";
import { Subscription } from "rxjs";
import { Socket } from "../../../infrastructure/socket.service";
import { UI } from "../../../services/ui.service";
import { UserService } from "../../../domain/user-service";
import { PopOverComponent } from "../../pop-over/pop-over.component";
import { IUploadFile } from "../../../domain/Upload/i-upload-file";
import { SubtitleRepository } from "../../../domain/subtitle-repository";
import { Subtitle } from "../../../domain/subtitle";
import { addWarning } from "@angular-devkit/build-angular/src/utils/webpack-diagnostics";

@Component({
  selector: "icn-edit-media-files-tab",
  templateUrl: "./edit-media-files-tab.component.html",
  styleUrls: ["./edit-media-files-tab.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditMediaFilesTabComponent
  implements OnChanges, OnInit, OnDestroy
{
  readonly bottom = 'bottom';

  constructor(
    private mediaRepository: MediaRepository,
    private uploadService: UploadService,
    private socketService: Socket,
    private changeDetectorRef: ChangeDetectorRef,
    private uiService: UI,
    private userService: UserService,
    private _subtitleRepository: SubtitleRepository,
  ) {}

  @Input() media: MediaInfo;
  @Output()
  audioLangChanged = new EventEmitter<void>();

  @ViewChild("subtitlesLanguagePopover")
  subtitlesLanguagePopover: PopOverComponent;

  @ViewChild("closedCaptionsLanguagePopover")
  closedCaptionsLanguagePopover: PopOverComponent;

  @ViewChild("languagePopover")
  languagePopover: PopOverComponent;

  @ViewChild("closedCaptionInput")
  closedCaptionInput: ElementRef<HTMLInputElement>;

  audioUploadedFiles: IUploadFile[] = [];
  packshotUploaded: IUploadFile;
  trailerUploadedFiles: IUploadFile[] = [];
  uploadedSubtitles: string[] = [];
  uploadedClosedCaptions: string[] = [];
  audioFileChosen;
  subtitleFileChosen;
  closedCaptionFileChosen;
  languages: [{ code: string; name: string }];
  private linkedFileCompleteSubscription: Subscription;
  private uploadFinishedChangeSubscription: Subscription;
  private packshotUploadFinishedChangeSubscription: Subscription;
  private packshotCompleteSubscription: Subscription;

  dragState = {
    subtitles: false,
    audio: false,
    packshot: false,
    trailer: false,
  };

  uploadMethods: { [elementType: string]: (file: File) => void } = {
    subtitles: (file: File) => {
      this.uploadSubtitle(file);
    },
    audio: (file: File) => {
      this.uploadAudioFile(file);
    },
    packshot: (file: File) => {
      this.uploadPackshotFile(file);
    },
    trailer: (file: File) => {
      this.uploadTrailerFile(file);
    },
    closedCaptions: (file: File) => {
      this.uploadClosedCaption(file);
    },
  };

  uploadSubtitle(file: File) {
    const mediaId = this.media.id;
    this.uploadedSubtitles.push(file.name);
    this.mediaRepository
      .uploadSubtitle(file, this.media.id)
      .then((result) => {
        if (!this.media || this.media.id !== mediaId) {
          return;
        }

        const indexInUploaded = this.uploadedSubtitles.findIndex(
          (x) => x === file.name,
        );
        if (indexInUploaded > -1) {
          this.uploadedSubtitles.splice(indexInUploaded, 1);
        }

        const subsWithSameId = this.media.subtitles.find(
          (x) => x.id === result.id,
        );
        if (subsWithSameId) {
          subsWithSameId.language = result.language;
          subsWithSameId.playbackSpeed = result.playbackSpeed;
          subsWithSameId.fps = result.fps;
        } else {
          this.media.subtitles.push(
            new Subtitle(
              result.id,
              result.language,
              result.offset,
              result.fps,
              result.playbackSpeed,
            ),
          );
        }
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const indexInUploaded = this.uploadedSubtitles.findIndex(
          (x) => x === file.name,
        );
        if (indexInUploaded > -1) {
          this.uploadedSubtitles.splice(indexInUploaded, 1);
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  uploadAudioFile(file: File) {
    const audioFile = this.uploadService.uploadLinkedFile(
      file,
      this.media.id,
      MediaLinkedType.Audio,
    );
    this.audioUploadedFiles.push(audioFile);
    this.changeDetectorRef.markForCheck();
  }

  showCannotUploadMoreThanOnePackshotAlert() {
    this.uiService.notify(this.uiService.EVENTS.ShowAlert, {
      title: "Upload packshot",
      message: "Cannot upload more than one packshot",
    });
  }

  uploadPackshotFile(file: File) {
    if (
      this.packshotUploaded ||
      (this.media && this.media.packshotIsAnalyzed)
    ) {
      this.showCannotUploadMoreThanOnePackshotAlert();
      return;
    }
    this.packshotUploaded = this.uploadService.uploadPackshot(
      file,
      this.media.id,
    );
    this.changeDetectorRef.markForCheck();
  }

  uploadTrailerFile(file: File) {
    const trailerFile = this.uploadService.uploadLinkedFile(
      file,
      this.media.id,
      MediaLinkedType.Trailer,
    );
    this.trailerUploadedFiles.push(trailerFile);
    this.changeDetectorRef.markForCheck();
  }

  removeSubtitle(subtitle) {
    this.uiService.notify(this.uiService.EVENTS.ShowConfirm, {
      title: "Delete Subtitle",
      message: "Are you sure about that?",
      buttons: [
        {
          title: "Yes",
          callback: () => {
            const mediaId = this.media.id;
            this.mediaRepository.removeSubtitle(subtitle.id).then(() => {
              if (!this.media || this.media.id !== mediaId) {
                return;
              }
              const index = this.media.subtitles.indexOf(subtitle);
              if (index > -1) {
                this.media.subtitles.splice(index, 1);
                this.changeDetectorRef.markForCheck();
              }
            });
          },
        },
        {
          title: "No",
        },
      ],
    });
  }

  onDrop(
    e: DragEvent,
    elementType:
      | "subtitles"
      | "audio"
      | "packshot"
      | "trailer"
      | "closedCaptions",
  ) {
    if (e.dataTransfer.files.length === 0) {
      return;
    }
    this.dragState[elementType] = false;
    this.changeDetectorRef.markForCheck();
    e.preventDefault();
    this.uploadMethods[elementType](e.dataTransfer.files[0]);
  }

  onInputFileChange(
    e,
    elementType:
      | "subtitles"
      | "audio"
      | "packshot"
      | "trailer"
      | "closedCaptions",
  ) {
    if (e.target.files.length === 0) {
      return;
    }

    this.uploadMethods[elementType](e.target.files[0]);
    e.target.value = "";
  }

  onDragEnter(dragElement: "subtitles" | "audio" | "packshot" | "trailer") {
    setTimeout(() => {
      this.dragState[dragElement] = true;
      this.changeDetectorRef.markForCheck();
    });
  }

  onDragLeave(dragElement: "subtitles" | "audio" | "packshot" | "trailer") {
    setTimeout(() => {
      this.dragState[dragElement] = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  getCompleteAudioFiles() {
    return this.media.audioFiles.filter(
      (x) => x.contentUploadStatus === ContentUploadStatus.Enabled,
    );
  }

  getAnalyzingAudioFiles() {
    return this.media.audioFiles.filter(
      (x) => x.contentUploadStatus === ContentUploadStatus.Pending,
    );
  }

  getCompleteTrailerFiles() {
    return this.media.trailers.filter(
      (x) => x.contentUploadStatus === ContentUploadStatus.Enabled,
    );
  }

  getAnalyzingTrailerFiles() {
    return this.media.trailers.filter(
      (x) => x.contentUploadStatus === ContentUploadStatus.Pending,
    );
  }

  abortAudioUpload(audioFile: IUploadFile) {
    const index = this.audioUploadedFiles.indexOf(audioFile);
    if (index > -1) {
      this.audioUploadedFiles.splice(index, 1);
      this.changeDetectorRef.markForCheck();
    }
    audioFile.abort();
  }

  abortPackshotUpload() {
    if (this.packshotUploaded) {
      this.packshotUploaded.abort();
      this.packshotUploaded = null;
    }
    this.changeDetectorRef.markForCheck();
  }

  abortTrailerUpload(trailerFile: IUploadFile) {
    const index = this.trailerUploadedFiles.indexOf(trailerFile);
    if (index > -1) {
      this.trailerUploadedFiles.splice(index, 1);
      this.changeDetectorRef.markForCheck();
    }
    trailerFile.abort();
  }

  onRemoveAudio(id) {
    this.uiService.notify(this.uiService.EVENTS.ShowConfirm, {
      title: "Delete Audio",
      message: "Are you sure about that?",
      buttons: [
        {
          title: "Yes",
          callback: async () => {
            const audioMediaInfo = await this.mediaRepository.getMediaInfo(id);
            audioMediaInfo.parentMediaId = null;
            await this.mediaRepository.update(audioMediaInfo);
            await this.mediaRepository.deleteMedia(id);
            if (!this.media) {
              return;
            }
            const index = this.media.audioFiles.findIndex((x) => x.id === id);
            if (index > -1) {
              this.media.audioFiles.splice(index, 1);
            }
            this.audioLangChanged.emit();
            this.changeDetectorRef.markForCheck();
          },
        },
        {
          title: "No",
        },
      ],
    });
  }

  onRemoveTrailer(id) {
    this.uiService.notify(this.uiService.EVENTS.ShowConfirm, {
      title: "Delete Trailer",
      message: "Are you sure about that?",
      buttons: [
        {
          title: "Yes",
          callback: async () => {
            const trailerMediaInfo =
              await this.mediaRepository.getMediaInfo(id);
            trailerMediaInfo.parentMediaId = null;
            await this.mediaRepository.update(trailerMediaInfo);
            await this.mediaRepository.deleteMedia(id);
            if (!this.media) {
              return;
            }
            const index = this.media.trailers.findIndex((x) => x.id === id);
            if (index > -1) {
              this.media.trailers.splice(index, 1);
            }
            this.changeDetectorRef.markForCheck();
          },
        },
        {
          title: "No",
        },
      ],
    });
  }

  trackById(index, item) {
    return item.id;
  }

  subscribeToEvents() {
    this.linkedFileCompleteSubscription =
      this.uploadService.linkedFileComplete.subscribe((result) => {
        if (!this.media.id || result.parentMediaId !== this.media.id) {
          return;
        }
        switch (result.mediaLinkedType) {
          case MediaLinkedType.Audio:
            {
              const index = this.audioUploadedFiles.findIndex(
                (x) => x.id === result.uploadId,
              );
              if (index > -1) {
                this.audioUploadedFiles.splice(index, 1);
              }
              this.media.audioFiles.push({
                id: result.mediaId,
                contentUploadStatus: ContentUploadStatus.Pending,
                name: result.name,
                language: "",
              });
              this.audioLangChanged.emit();
            }
            break;

          case MediaLinkedType.Trailer:
            {
              const index = this.trailerUploadedFiles.findIndex(
                (x) => x.id === result.uploadId,
              );
              if (index > -1) {
                this.trailerUploadedFiles.splice(index, 1);
              }
              this.media.trailers.push({
                id: result.mediaId,
                contentUploadStatus: ContentUploadStatus.Pending,
                thumbnailUrl: "",
                name: "",
              });
            }
            break;
        }
        this.changeDetectorRef.markForCheck();
      });

    this.packshotCompleteSubscription =
      this.uploadService.packshotComplete.subscribe((result) => {
        if (!this.media.id || result.mediaId !== this.media.id) {
          return;
        }
        this.packshotUploaded = null;
        this.media.packshotIsAnalyzed = true;
        this.changeDetectorRef.markForCheck();
      });

    this.uploadFinishedChangeSubscription =
      this.socketService.uploadFinishedChange.subscribe((result) => {
        if (!this.media.id || result.parentMediaId !== this.media.id) {
          return;
        }

        switch (result.mediaLinkedType) {
          case MediaLinkedType.Audio:
            {
              const audioFile = {
                id: result.id,
                name: result.name || result.userFilename,
                contentUploadStatus: result.contentUploadStatus,
                language: "",
              };

              const index = this.media.audioFiles.findIndex(
                (x) => x.id === result.id,
              );
              if (index > -1) {
                this.media.audioFiles.splice(index, 1, audioFile);
              } else {
                this.media.audioFiles.push(audioFile);
              }
              this.audioLangChanged.emit();
            }
            break;

          case MediaLinkedType.Trailer:
            {
              const trailer = {
                id: result.id,
                thumbnailUrl: result.contentSnapshotUrl,
                contentUploadStatus: result.contentUploadStatus,
                name: result.name,
              };

              const index = this.media.trailers.findIndex(
                (x) => x.id === result.id,
              );
              if (index > -1) {
                this.media.trailers.splice(index, 1, trailer);
              } else {
                this.media.trailers.push(trailer);
              }
            }
            break;
        }
        this.changeDetectorRef.markForCheck();
      });

    this.packshotUploadFinishedChangeSubscription =
      this.socketService.packshotUploadFinishedChange.subscribe((result) => {
        if (!this.media.id || result.mediaId !== this.media.id) {
          return;
        }

        this.media.packshotIsAnalyzed = false;
        if (result.uploadSuccess) {
          this.media.contentSnapshotUrl = result.url;
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes["media"]) {
      return;
    }

    this.audioUploadedFiles = this.media
      ? this.uploadService.getLinkedFilesOf(
          this.media.id,
          MediaLinkedType.Audio,
        )
      : [];
    this.packshotUploaded = this.media
      ? this.uploadService.getPackshotOf(this.media.id)
      : null;
    this.trailerUploadedFiles = this.media
      ? this.uploadService.getLinkedFilesOf(
          this.media.id,
          MediaLinkedType.Trailer,
        )
      : [];
    this.uploadedSubtitles = [];
    this.uploadedClosedCaptions = [];
  }

  ngOnInit() {
    this.languages = this.userService.getCurrentUser().languages;
    console.log(this.media);
    this.subscribeToEvents();
  }

  ngOnDestroy() {
    if (this.linkedFileCompleteSubscription) {
      this.linkedFileCompleteSubscription.unsubscribe();
    }

    if (this.uploadFinishedChangeSubscription) {
      this.uploadFinishedChangeSubscription.unsubscribe();
    }

    if (this.packshotCompleteSubscription) {
      this.packshotCompleteSubscription.unsubscribe();
    }

    if (this.packshotUploadFinishedChangeSubscription) {
      this.packshotUploadFinishedChangeSubscription.unsubscribe();
    }
  }

  onChangeLanguage(values, audioFile) {
    this.audioFileChosen = audioFile;
    this.audioLangChanged.emit();
    this.languagePopover.toggle(values);
  }

  onUploadClosedCaption() {
    if (this.closedCaptionInput) {
      this.closedCaptionInput.nativeElement.value = "";
      this.closedCaptionInput.nativeElement.click();
    }
  }

  uploadClosedCaption(file: File) {
    if (!file.name.toLowerCase().endsWith(".scc")) {
      this.uiService.notify(this.uiService.EVENTS.ShowAlert, {
        title: "Invalid Closed Caption File",
        message: "Only .scc files can be uploaded as closed captions.",
      });
      return;
    }

    const mediaId = this.media.id;
    this.uploadedClosedCaptions.push(file.name);
    this.uploadService
      .uploadClosedCaption(file, this.media.id)
      .then((result) => {
        if (!this.media || this.media.id !== mediaId) {
          return;
        }

        const indexInUploaded = this.uploadedClosedCaptions.findIndex(
          (x) => x === file.name,
        );
        if (indexInUploaded > -1) {
          this.uploadedClosedCaptions.splice(indexInUploaded, 1);
        }

        const existing = this.media.closedCaptions.find(
          (x) => x.id === result.id,
        );
        if (existing) {
          existing.language = result.language;
          existing.playbackSpeed = result.playbackSpeed;
          existing.fps = result.fps;
        } else {
          this.media.closedCaptions.push(
            new Subtitle(
              result.id,
              result.language,
              result.offset,
              result.fps,
              result.playbackSpeed,
              undefined,
              undefined,
              result.flavor,
            ),
          );
        }
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const indexInUploaded = this.uploadedClosedCaptions.findIndex(
          (x) => x === file.name,
        );
        if (indexInUploaded > -1) {
          this.uploadedClosedCaptions.splice(indexInUploaded, 1);
        }
        this.uiService.notify(this.uiService.EVENTS.ShowAlert, {
          title: "Closed Caption Upload Failed",
          message: (err && err.message) || "general error",
        });
        this.changeDetectorRef.markForCheck();
      });
  }

  onClosedCaptionSettingsClick(event, captionFile) {
    captionFile.fileName =
      this.media.tenantMediaId + `_${captionFile.flavoredLabel}.json`;
    this._subtitleRepository.getSubtitleURL(captionFile.id).then((caption) => {
      this.closedCaptionFileChosen = captionFile;
      this.closedCaptionFileChosen.signedUrl = caption.url;
      this.closedCaptionsLanguagePopover.toggle(event);
    });
    this._subtitleRepository
      .getClosedCaptionOriginalURL(captionFile.id)
      .then((original) => {
        captionFile.originalUrl =
          original && original.url ? original.url : null;
        this.changeDetectorRef.markForCheck();
      });
  }

  onClosedCaptionsSelectedValueChange(langCode) {
    if (
      this.media.closedCaptions.findIndex((x) => x.language === langCode) >= 0
    ) {
      this.uiService.notify(this.uiService.EVENTS.ShowAlert, {
        title: "Can't Choose This Language",
        message:
          "Language already exists on another closed caption of this media.",
      });
    } else {
      this.closedCaptionFileChosen.code = langCode;
      this.closedCaptionFileChosen.name =
        this.languages[
          this.languages.findIndex((x) => x.code === langCode)
        ].name;
      this.closedCaptionFileChosen.language = langCode;
    }
  }

  removeClosedCaption(caption) {
    this.uiService.notify(this.uiService.EVENTS.ShowConfirm, {
      title: "Delete Closed Caption",
      message: "Are you sure about that?",
      buttons: [
        {
          title: "Yes",
          callback: () => {
            const mediaId = this.media.id;
            this.mediaRepository.removeSubtitle(caption.id).then(() => {
              if (!this.media || this.media.id !== mediaId) {
                return;
              }
              const index = this.media.closedCaptions.indexOf(caption);
              if (index > -1) {
                this.media.closedCaptions.splice(index, 1);
                this.changeDetectorRef.markForCheck();
              }
            });
          },
        },
        {
          title: "No",
        },
      ],
    });
  }

  onSubtitleSettingsClick(event, subtitleFile) {
    subtitleFile.fileName =
      this.media.tenantMediaId + `_${subtitleFile.language}.json`;
    this._subtitleRepository
      .getSubtitleURL(subtitleFile.id)
      .then((subtitle) => {
        this.subtitleFileChosen = subtitleFile;
        this.subtitleFileChosen.signedUrl = subtitle.url;
        this.subtitlesLanguagePopover.toggle(event);
      });
  }

  onSubtitlesSelectedValueChange(langCode) {
    if (this.media.subtitles.findIndex((x) => x.language === langCode) >= 0) {
      this.uiService.notify(this.uiService.EVENTS.ShowAlert, {
        title: "Can't Choose This Language",
        message: "Language already exists on another subtitle of this media.",
      });
    } else {
      this.subtitleFileChosen.code = langCode;
      this.subtitleFileChosen.name =
        this.languages[
          this.languages.findIndex((x) => x.code === langCode)
        ].name;
      this.subtitleFileChosen.language = langCode;
    }
  }
}
